/** Reusable tag-condition builder — committed chips in one bordered container
 *  (QuerySearch look) with an inline AND/OR toggle, plus a staged builder
 *  panel (tenant → category → tag) in a Popper anchored under the container.
 *
 * Chips are either PINNED to one concrete tag (bold `#` sigil — matches only
 * that tenant's exact tag) or system-wide BY-CODE specs (dashed border,
 * monospace category code — re-resolved against whichever tenant is rendering
 * at evaluation time). Data access is fully injected via `fetchers`; the
 * widget never imports API modules.
 */
import {
  Box,
  Chip,
  ClickAwayListener,
  FormHelperText,
  Paper,
  Popper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

import { specKey } from './convert';
import { SpecBuilderPanel } from './SpecBuilderPanel';
import {
  isCanonicalSpec,
  type TagChip,
  type TagConditionFetchers,
  type TagConditionValue,
} from './types';

interface Props {
  value: TagConditionValue;
  onChange: (value: TagConditionValue) => void;
  fetchers: TagConditionFetchers;
  label?: string;
  disabled?: boolean;
  helperText?: string;
  /** Tenant-scoped editors: pin the concrete-tag path to this tenant — see
   *  SpecBuilderPanel. Omit for system-scoped hosts (full tenant stage). */
  fixedTenant?: { id: number };
  /** Hide the AND/OR toggle for hosts whose chips are a plain SET rather than
   *  a condition — the notification `tag` block merges every referenced tag's
   *  data, so a match mode would be a control that does nothing. `matchMode`
   *  is still carried in the value (ignored by such hosts). */
  hideMatchMode?: boolean;
}

function chipTooltip(chip: TagChip): string {
  const triple = [chip.labels.tenant ?? 'any tenant', chip.labels.category, chip.labels.tag].join(
    ' · ',
  );
  if ('tag_id' in chip.spec) return `${triple} — pinned tag #${chip.spec.tag_id}`;
  if (isCanonicalSpec(chip.spec)) {
    return `${triple} — matches every tag name that normalises to this canonical`;
  }
  return `${triple} — matches by category code + tag name`;
}

function chipLabel(chip: TagChip) {
  const byId = 'tag_id' in chip.spec;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
      {chip.labels.tenant !== null && (
        <>
          <Box component="span">{chip.labels.tenant}</Box>
          <Box component="span" sx={{ color: 'text.disabled' }}>
            ·
          </Box>
        </>
      )}
      <Box component="span" sx={byId ? undefined : { fontFamily: 'monospace' }}>
        {chip.labels.category}
      </Box>
      <Box component="span" sx={{ color: 'text.disabled' }}>
        ·
      </Box>
      {byId && (
        <Box
          component="span"
          title="Pinned to this exact tag"
          sx={{ fontWeight: 700, color: 'primary.main' }}
        >
          #
        </Box>
      )}
      <Box component="span">{chip.labels.tag}</Box>
    </Box>
  );
}

export function TagConditionInput({
  value,
  onChange,
  fetchers,
  label,
  disabled,
  helperText,
  fixedTenant,
  hideMatchMode,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const openPanel = () => {
    if (!disabled) setOpen(true);
  };

  const commitChip = (chip: TagChip) => {
    // Dedup by spec identity — committing an existing chip is a no-op.
    const key = specKey(chip.spec);
    if (value.chips.some((c) => specKey(c.spec) === key)) return;
    onChange({ ...value, chips: [...value.chips, chip] });
  };

  const deleteChip = (index: number) =>
    onChange({ ...value, chips: value.chips.filter((_, i) => i !== index) });

  const popLastChip = () => {
    if (value.chips.length > 0) onChange({ ...value, chips: value.chips.slice(0, -1) });
  };

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.25 }}>
          {label}
        </Typography>
      )}
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box>
          <Box
            ref={containerRef}
            role="group"
            aria-label={label ?? 'Tag condition'}
            aria-expanded={open}
            tabIndex={disabled ? undefined : 0}
            onClick={openPanel}
            onFocus={openPanel}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && open) {
                e.stopPropagation();
                setOpen(false);
              }
            }}
            // Container look copied from QuerySearchInput (chips-in-one-box).
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.5,
              minHeight: 40,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              border: 1,
              borderColor: focused || open ? 'primary.main' : 'divider',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              '&:focus-within': {
                borderColor: 'primary.main',
                boxShadow: (t) => `0 0 0 1px ${t.palette.primary.main}`,
              },
            }}
          >
            {value.chips.map((chip, i) => (
              <Tooltip key={specKey(chip.spec)} title={chipTooltip(chip)}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={chipLabel(chip)}
                  onDelete={disabled ? undefined : () => deleteChip(i)}
                  // Deleting a chip must not pop the builder panel open —
                  // neither via the click nor via the focus it gives the chip.
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  sx={{
                    maxWidth: 280,
                    ...('tag_id' in chip.spec ? {} : { borderStyle: 'dashed' }),
                  }}
                />
              </Tooltip>
            ))}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ userSelect: 'none' }}>
              {value.chips.length === 0 ? 'Add tag…' : '+ Add tag…'}
            </Typography>
            <Box
              sx={{ ml: 'auto', display: hideMatchMode ? 'none' : undefined }}
              // The mode toggle must neither open nor close the panel.
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
            >
              <Tooltip title="Require ALL chips to match (AND), or ANY of them (OR)">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={value.matchMode}
                  disabled={disabled || value.chips.length < 2}
                  onChange={(e, v: 'and' | 'or' | null) => {
                    e.preventDefault();
                    if (v) onChange({ ...value, matchMode: v });
                  }}
                  aria-label="tag match mode"
                  sx={{
                    '& .MuiToggleButton-root': { py: 0, px: 0.75, fontSize: 11, lineHeight: 1.6, border: 0 },
                  }}
                >
                  <ToggleButton value="and" aria-label="all tags must match">
                    AND
                  </ToggleButton>
                  <ToggleButton value="or" aria-label="any tag matches">
                    OR
                  </ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
            </Box>
          </Box>
          <Popper
            open={open && !disabled}
            anchorEl={containerRef.current}
            placement="bottom-start"
            // Must beat the Dialog (modal = 1300) — this widget lives in dialogs.
            sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
          >
            <Paper
              elevation={4}
              onMouseDown={(e) => {
                // Keep clicks inside the panel from re-triggering container focus.
                e.stopPropagation();
              }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              sx={{ width: 480, maxWidth: '92vw', p: 1.5, mt: 0.5 }}
            >
              <SpecBuilderPanel
                fetchers={fetchers}
                onCommit={commitChip}
                onClose={() => setOpen(false)}
                onBackspaceAtRoot={popLastChip}
                fixedTenant={fixedTenant}
              />
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
}
